/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import clsx from "clsx";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {Reservation} from "types/entities/Reservation";
import {useTranslation} from "react-i18next";
import Loader from "components/Loader";
import {ReservationOfferStatus} from "types/enums/ReservationOfferStatus";
import button from "components/Button";
import {useDispatch} from "useDispatch";
import {ArrowPathIcon, CheckIcon, PaperAirplaneIcon, PlusIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {closeModal, openModal} from "helpers/actions";
import {ModalSize} from "pages/Front/components/Popup";
import BookingSellerStatusReasonModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerStatusReasonModal";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";

interface BookingSellerReservationInfoCardProps {
    reservation: Reservation;
    className?: string;
}

const BookingSellerReservationOfferStatusCard = (props: BookingSellerReservationInfoCardProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeRequestStatus, setActiveRequestStatus] = useState<ReservationOfferStatus | null>(null);
    const [activeOfferStatus, setActiveOfferStatus] = useState<ReservationOfferStatus>(props.reservation.offer);
    const showReconfirmButton: boolean = activeOfferStatus === ReservationOfferStatus.Accepted;

    if (!props.reservation) {
        return <Loader />;
    }

    interface OfferStatusProps {
        status: ReservationOfferStatus;
        color: string;
    }

    const offers: OfferStatusProps[] = [
        {status: ReservationOfferStatus.New, color: "blue"},
        {status: ReservationOfferStatus.Sent, color: "purple"},
        {status: ReservationOfferStatus.Accepted, color: "green"},
        {status: ReservationOfferStatus.Declined, color: "red"},
        {status: ReservationOfferStatus.Reconfirm, color: "teal"},
    ];

    const setOfferStatusHandler = (offerStatus: ReservationOfferStatus) => {
        if (isLoading) {
            return;
        }

        if (offerStatus === ReservationOfferStatus.Declined) {
            dispatch(
                openModal(
                    <BookingSellerStatusReasonModal
                        offerStatus={offerStatus}
                        reservation={props.reservation}
                    />,
                    {
                        modalSize: ModalSize.Small,
                        closeModal: () => {
                            dispatch(closeModal());
                        },
                    }
                ),
            );
        } else if (offerStatus === ReservationOfferStatus.Reconfirm) {
            // todo: add request for reconfirm
            console.log("reconfirm");
            setOfferStatus(offerStatus).then(r => {
            });

        } else {
            setOfferStatus(offerStatus).then(r => {
            });
        }
    };

    const setOfferStatus = async (offerStatus: ReservationOfferStatus) => {
        setIsLoading(true);
        setActiveRequestStatus(offerStatus);
        const r = await dispatch(BookingLoginAction.fetchSetReservationOfferStatus(props.reservation.id, offerStatus))
            .finally(() => {
                setIsLoading(false);
                setActiveRequestStatus(null);
            });
        setActiveOfferStatus(offerStatus);
    };

    return (
        <BookingCard className={clsx("BookingSellerReservationOfferStatusCard !px-0 !py-0 mb-6", props.className)}>
            <BookingCardTitle className={"!font-bold text-xl !text-black px-3 py-2.5 xl:px-3 xl:py-3 border-b"}
                              title={t("Reservation offer status")} />
            <div
                className={clsx(
                    "grid gap-3 px-3 py-3",
                    showReconfirmButton ? "sm:grid-cols-2 md:grid-cols-5" : "sm:grid-cols-2 md:grid-cols-4",
                )}
            >
                {offers.map((offer, index) => {

                    if (!showReconfirmButton && offer.status === ReservationOfferStatus.Reconfirm) {
                        return;
                    }

                    return (
                        <button
                            className={clsx(
                                "px-3 py-2 text-white rounded text-base capitalize inline-flex items-center justify-center font-bold",
                                activeOfferStatus === offer.status ? `bg-${offer.color}-400` : "bg-gray-400/90",
                                `hover:bg-${offer.color}-500`,
                            )}
                            key={offer.status}
                            onClick={() => setOfferStatusHandler(offer.status)}
                        >
                            {isLoading && offer.status === activeRequestStatus
                                ? (
                                    <svg className="animate-spin flex-none w-4 h-4" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path
                                            className="opacity-75"
                                            d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                )
                                : (<>
                                    {offer.status === ReservationOfferStatus.New &&
                                        <PlusIcon className={"w-4 h-4 flex-none"} />}
                                    {offer.status === ReservationOfferStatus.Sent &&
                                        <PaperAirplaneIcon className={"w-4 h-4 flex-none"} />}
                                    {offer.status === ReservationOfferStatus.Accepted &&
                                        <CheckIcon className={"w-4 h-4 flex-none"} />}
                                    {offer.status === ReservationOfferStatus.Declined &&
                                        <XMarkIcon className={"w-4 h-4 flex-none"} />}
                                    {offer.status === ReservationOfferStatus.Reconfirm &&
                                        <ArrowPathIcon className={"w-4 h-4 flex-none"} />}
                                </>)
                            }

                            <span className={"ml-1.5"}>{t(offer.status)}</span>
                        </button>
                    );
                })}
            </div>
            {/* css class helper */}
            <div className={"hidden hover:bg-purple-500 bg-purple-400 bg-teal-400 hover:bg-teal-500"}></div>
        </BookingCard>
    );
};

export default BookingSellerReservationOfferStatusCard;
